import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Layout, Type, Link as LinkIcon, ExternalLink, Moon, Sun } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const MockCard = ({ title, desc, url, icon: Icon, delay = 0, x, y, rotate, scale = 1 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, rotate: rotate - 5, scale: scale * 0.9 }}
    animate={{ opacity: 1, y: 0, rotate: rotate, scale: scale }}
    transition={{ duration: 0.6, delay, type: "spring" }}
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: '240px',
      padding: '20px',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: '12px',
      boxShadow: '0 8px 30px var(--shadow-color)',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transformOrigin: 'center center'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ 
        width: '32px', 
        height: '32px', 
        borderRadius: '6px', 
        background: 'var(--accent-color)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Icon size={16} />
      </div>
      <span style={{ fontWeight: 600, fontSize: '15px' }}>{title}</span>
    </div>
    <p style={{ margin: 0, fontSize: '13px', color: 'gray', lineHeight: '1.4' }}>
      {desc}
    </p>
    {url && (
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
         <span style={{ fontSize: '11px', color: 'gray' }}>{url}</span>
         <ExternalLink size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />
      </div>
    )}
  </motion.div>
);

const Home = () => {
  const { theme, toggleTheme } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-color)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Background Grid Pattern (Subtle) */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.05,
        backgroundImage: 'linear-gradient(var(--text-color) 1px, transparent 1px), linear-gradient(90deg, var(--text-color) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none'
      }} />

      {/* Top Navigation / Logo */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
            position: 'absolute',
            top: isMobile ? '20px' : '40px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 10
        }}
      >
        <img 
            src={theme === 'dark' ? '/white-logo.png' : '/black-logo.png'} 
            alt="Canvmark Logo" 
            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
        />
        <span style={{ 
            fontSize: '24px', 
            fontWeight: 700, 
            letterSpacing: '-0.02em',
            color: 'var(--text-color)'
        }}>
            Canvmark
        </span>
      </motion.div>

      <div style={{
        maxWidth: '1200px',
        width: '100%',
        padding: isMobile ? '0 20px' : '0 40px',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '40px' : '60px',
        alignItems: 'center',
        zIndex: 2,
        height: isMobile ? '100%' : 'auto',
        overflowY: isMobile ? 'auto' : 'visible', // Allow scrolling on mobile if content overflows height
        paddingTop: isMobile ? '80px' : '0'
      }}>
        {/* Left: Content */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{
              textAlign: isMobile ? 'center' : 'left',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isMobile ? 'center' : 'flex-start',
              marginTop: isMobile ? '20px' : '0'
          }}
        >
          <motion.h1 
            style={{ 
              fontSize: isMobile ? '2.5rem' : '4rem', 
              fontWeight: 800, 
              letterSpacing: '-0.03em', 
              lineHeight: 1.1,
              marginBottom: '24px',
              background: 'linear-gradient(to right, var(--text-color), gray)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Organize<br/>your bookmarks<br/>spatially.
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            style={{ 
              fontSize: isMobile ? '1rem' : '1.2rem', 
              color: 'gray', 
              marginBottom: '40px', 
              maxWidth: '480px',
              lineHeight: 1.6 
            }}
          >
            An infinite canvas for your favorite websites. 
            Minimal, fast, and 100% private with local storage.
          </motion.p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Link to="/canvas" style={{ textDecoration: 'none' }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '16px 32px',
                  fontSize: '18px',
                  fontWeight: 600,
                  backgroundColor: 'var(--text-color)',
                  color: 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 10px 20px var(--shadow-color)'
                }}
              >
                Start Organizing <ArrowRight size={20} />
              </motion.button>
            </Link>

            <motion.button
              onClick={toggleTheme}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-color)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px var(--shadow-color)'
              }}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? <Moon size={24} /> : <Sun size={24} />}
            </motion.button>
          </div>
        </motion.div>

        {/* Right: Visual Preview */}
        <div style={{ 
            position: 'relative', 
            height: isMobile ? '300px' : '500px', 
            width: '100%',
            display: isMobile ? 'flex' : 'block',
            justifyContent: 'center',
            // On mobile, we might need to adjust offsets
        }}>
          <MockCard 
            title="Design Resources" 
            desc="Collection of best minimal design systems." 
            icon={Layout} 
            x={isMobile ? "5%" : "10%"} 
            y={isMobile ? "10%" : "20%"} 
            rotate={-3}
            delay={0.4}
            scale={isMobile ? 0.7 : 1}
          />
          <MockCard 
            title="Dev Docs" 
            desc="React and Framer Motion API." 
            icon={LinkIcon} 
            x={isMobile ? "45%" : "45%"} 
            y={isMobile ? "30%" : "40%"} 
            rotate={2}
            delay={0.6}
            scale={isMobile ? 0.7 : 1}
          />
          <MockCard 
            title="Inspiration" 
            desc="Awnn.io - Award winning websites."
            url="awwwards.com" 
            icon={ExternalLink} 
            x={isMobile ? "15%" : "20%"} 
            y={isMobile ? "55%" : "60%"} 
            rotate={-2}
            delay={0.8}
            scale={isMobile ? 0.7 : 1}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;
